import { parseISO } from 'date-fns';
import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/system';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';

import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import { GameModel } from '../types';

import { getTeamLogoUrl, teamLogoSize } from '@/shared/services/getTeamLogoUrl';
import { useVenueAwareTimeFormatter } from '../hooks/useVenueAwareTimeFormatter';
import { getDuration } from '@/shared/services/getDuration';
import { UpcomingIndicator } from './upcomingIndicator';
import { TeamModel } from '@/features/teams/types/TeamModel';
import { generateGradient } from '../services/generateGradient';
import { LocalSessionState, useAuth } from '@/features/scaffolding/components/AuthContext';
import { Tracking } from '@/shared/services/tracking';
import { LiveDot } from './LiveDot';
import TeamRecord from './TeamRecord';
import { cx } from '@/styles/muiTheme';

type Props = {
  game: GameModel;
  liveScore?: any;
  cardWidth?: number;
  cardHeight?: number;
};

// The card fills its grid column; only the height is pinned so rows stay even.
const CARD_HEIGHT = 210;
// Rendered size of the team crest. Single knob — no transform scaling on top.
const LOGO_SIZE = 48;

// Keyframe animations
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const calculateRefetchInterval = (startDate: Date): number => {
  const minutesUntilGame = (new Date(startDate).getTime() - Date.now()) / (1000 * 60);
  if (minutesUntilGame > 1440) return 1000 * 60 * 60; // 1 hour
  if (minutesUntilGame > 60) return 1000 * 60 * 10; // 10 minutes
  if (minutesUntilGame > 10) return 1000 * 60; // 1 minute
  return 1000 * 3; // 3 seconds
};

const GameCard = ({ game, liveScore }: Props) => {
  const auth = useAuth();
  const { formatTime } = useVenueAwareTimeFormatter();

  // Handle both Date and string types for timestamps
  const start = game.startTimestamp instanceof Date ? game.startTimestamp : parseISO(game.startTimestamp || '');
  const end = game.endTimestamp
    ? (game.endTimestamp instanceof Date ? game.endTimestamp : parseISO(game.endTimestamp))
    : undefined;

  const [isLoaded, setIsLoaded] = useState(false);

  const gameStatus = useMemo(() => game.status || 'recorded', [game.status]);
  const isClickable = useMemo(
    () =>
      ['live', 'recorded'].includes(gameStatus) && auth.sessionState === LocalSessionState.READY,
    [gameStatus, auth.sessionState],
  );

  // The day is already carried by the sticky rail beside the grid, so the card
  // header only needs the clock time. `time-only` formats as `h:mm a zzz`, which
  // we split on the last space so the zone abbreviation can be dimmed.
  const [timeLabel, tzLabel] = useMemo(() => {
    const formatted = formatTime(start, { formatType: 'time-only', venueTimezone: null });
    const split = formatted.lastIndexOf(' ');
    return split === -1 ? [formatted, ''] : [formatted.slice(0, split), formatted.slice(split + 1)];
  }, [start, formatTime]);

  const homeTeamPrimaryColor = useMemo(
    () =>
      game.homeTeamColor ||
      game.homeTeam?.teamColors?.find((tc) => tc.type === 'primary')?.hex_color ||
      '#1a1a2e',
    [game.homeTeam?.teamColors, game.homeTeamColor],
  );

  const awayTeamPrimaryColor = useMemo(
    () =>
      game.awayTeamColor ||
      game.awayTeam?.teamColors?.find((tc) => tc.type === 'primary')?.hex_color ||
      '#16213e',
    [game.awayTeam?.teamColors, game.awayTeamColor],
  );

  const updateGameStatus = useCallback(async () => {
    if (Number.isNaN(game.id)) return {};
    const { data } = await get<GameModel>(`/v1.0/games/${game.uniqueId}?polling=true`);
    return data;
  }, [game.id, game.uniqueId]);

  const isWithinNextHour = useMemo(() => {
    const now = new Date();
    const oneHourFromNow = new Date(now.getTime() + 1 * 60 * 60 * 1000);
    return start <= oneHourFromNow && start >= now;
  }, [start]);

  useQuery({
    queryKey: ['GamesStatus', game.id],
    queryFn: updateGameStatus,
    refetchOnWindowFocus: true,
    enabled: gameStatus === 'scheduled' && isWithinNextHour,
    refetchInterval: calculateRefetchInterval(start),
  });

  const gradientBackground = useMemo(() => {
    return generateGradient(homeTeamPrimaryColor, awayTeamPrimaryColor, 0.85, 8);
  }, [homeTeamPrimaryColor, awayTeamPrimaryColor]);


  const renderTeamInfo = useCallback(
    (team: TeamModel, isHome: boolean) => {
  
      const homeScore = liveScore ? liveScore.homeScore : game.homeTeamScore;
      const awayScore = liveScore ? liveScore.awayScore : game.awayTeamScore;

      const score = isHome ? homeScore : awayScore;
      const opponentScore = isHome ? awayScore : homeScore;
      const isWinner = gameStatus !== 'scheduled' && score > opponentScore;
      const isLoser = gameStatus !== 'scheduled' && score < opponentScore;

      return (
        <Box
          key={team.id}
          sx={{
            display: 'flex',
            flexDirection: isHome ? 'row' : 'row-reverse',
            alignItems: 'center',
            // Each side claims half the row, then splits it: the crest is pushed
            // to the outer (card) edge and the score to the inner edge beside the
            // dash. Scheduled games render no score, and `space-between` with a
            // lone child would pin the crest to the wrong side, so they centre.
            flex: 1,
            justifyContent: gameStatus === 'scheduled' ? 'center' : 'space-between',
            gap: 2,
            position: 'relative',
            animation: `${slideUp} 0.6s ease-out ${isHome ? '0.1s' : '0.2s'} both`,
          }}
        >
          {/* Score */}
          {gameStatus !== 'scheduled' && (
            <Box
              sx={{
                //  background: isWinner
                //    ? 'linear-gradient(135deg, #4CAF50, #45a049)'
                //    : 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                //borderRadius: '12px',
                pb: 5,
                //py: 1,
                //  border: isWinner ? '2px solid #4CAF50' : '2px solid rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                minWidth: 24,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: '1.7rem',
                  fontWeight: 500,
                  color: isWinner ? 'white' : 'rgba(255,255,255,0.4)',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                {score}
              </Typography>
            </Box>
          )}

          {/* Team info container */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              filter: isLoser ? 'grayscale(0.5) brightness(0.7)' : 'none',
            }}
          >
            {/* Team logo */}
            <Box
              sx={{
                position: 'relative',
                mb: 0.5,
                // Soft white glow behind the logo
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '120%',
                  height: '120%',
                  borderRadius: '50%',
                  background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.18) 0%, transparent 70%)',
                  filter: 'blur(6px)',
                  pointerEvents: 'none',
                },
              }}
            >
              <Box
                component="img"
                src={getTeamLogoUrl(team)}
                alt={team.abbreviation}
                onLoad={() => setIsLoaded(true)}
                sx={{
                  ...teamLogoSize(LOGO_SIZE),
                  position: 'relative',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                  transition: 'all 0.3s ease',
                  animation: isLoaded ? `${scaleIn} 0.5s ease-out` : 'none',
                }}
              />
            </Box>

            {/* Team abbreviation */}
            <Typography
              variant="h6"
              sx={{
                fontSize: '0.9rem',
                fontWeight: 700,
                color: 'white',
                textAlign: 'center',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              }}
            >
              {team.abbreviation}
            </Typography>

            {/* Season record, W-L-OTL. Division placement is left to the game
                page scoreboard — the card's two-column layout has no room for
                it. Only NHL teams carry a `standing` today (it is the only
                league the standings cron pulls), and it is null until the first
                pull of a season, so the whole line is optional rather than
                league-gated. */}
            {team.standing && (
              <TeamRecord standing={team.standing} align="center" showPlacement={false} />
            )}
          </Box>
        </Box>
      );
    },
    [game.homeTeamScore, game.awayTeamScore, gameStatus, isLoaded],
  );

  const getStatusIndicator = () => {
    switch (gameStatus) {
      case 'live':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LiveDot/>
            {/* Matches the start-time treatment in the opposite corner. */}
            <Typography
              sx={{
                fontFamily: cx.fontMono,
                fontSize: 12,
                fontWeight: 500,
                color: 'rgba(255,255,255,0.8)',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                lineHeight: 1,
              }}
            >
              {liveScore?.period} {liveScore?.time}
            </Typography>
          </Box>
        );
      case 'recorded':
        return (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.5)',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              lineHeight: 1,
            }}
          >
            Final
          </Typography>
        );
      case 'scheduled':
        return <UpcomingIndicator color="#FFA726" />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        animation: `${slideUp} 0.8s ease-out`,
      }}
    >
      {/* Overlay for locked state */}
      {auth.sessionState !== LocalSessionState.READY && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
            borderRadius: 1,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 400,
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            }}
          >
            Loading...
          </Typography>
        </Box>
      )}

      <Link
        to={isClickable ? `/games/${game.uniqueId}` : '#'}
        style={{ textDecoration: 'none' }}
        onClick={(e) => {
          if (!isClickable) e.preventDefault();
          Tracking.getInstance().track(gameStatus === 'live' ? 'Join Game' : 'Watch Game', {
            category: 'Games',
            game: `${game.homeTeam.name} vs ${game.awayTeam.name}`,
          });
        }}
      >
        <Card
          sx={{
            width: '100%',
            height: CARD_HEIGHT,
            background: gradientBackground,
            // The `background` shorthand resets background-origin to padding-box,
            // so with a border the gradient is sized to the padding box but still
            // painted across the border box — and background-repeat (also reset to
            // `repeat`) tiles the leftover 1px strips with the *opposite* end of
            // the gradient. Sizing it to the border box removes the strips.
            backgroundOrigin: 'border-box',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
            cursor: isClickable ? 'pointer' : 'default',
            pointerEvents: isClickable ? 'auto' : 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            filter: gameStatus === 'scheduled' ? 'grayscale(0.3) brightness(0.8)' : 'none',
            opacity: gameStatus === 'scheduled' ? 0.8 : 1,
            // Hover is a border lift only. Transparent at rest so the card reads
            // as pure gradient; translucent white on hover rather than a fixed
            // grey so it lands the same over every team-colour gradient.
            border: '1px solid transparent',
            transition: 'border-color 0.2s ease',
            '&:hover': { borderColor: 'rgba(255,255,255,0.32)' },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
              pointerEvents: 'none',
            },
          }}
        >
          <CardContent
            sx={{
              position: 'relative',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              '&:last-child': { pb: 1.75 },
            }}
          >
            {/* Header: start time + zone on the left, Final / live badge on the right */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: cx.fontMono,
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#FFFFFF',
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  }}
                >
                  {timeLabel}
                </Typography>
                {tzLabel && (
                  <Typography
                    component="span"
                    sx={{ fontSize: 10, color: 'rgba(255,255,255,0.55)' }}
                  >
                    {tzLabel}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>{getStatusIndicator()}</Box>
            </Box>

            {/* Teams container */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
                position: 'relative',
                gap: gameStatus === 'scheduled' ? 2 : 0.5,
              }}
            >
              {/* VS indicator for scheduled games */}
              {gameStatus === 'scheduled' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // border: '2px solid rgba(255,255,255,0.2)',
                    // backdropFilter: 'blur(10px)',
                    zIndex: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                    }}
                  >
                    VS
                  </Typography>
                </Box>
              )}

              {/* Away team */}
              {renderTeamInfo(game.awayTeam, false)}

              {/* Score separator dash. Carries the same pb as the score boxes so
                  it rides at the digits' height rather than the row's centre. */}
              {gameStatus !== 'scheduled' && (
                <Typography
                  sx={{
                    pb: 5,
                    color: 'rgba(255,255,255,0.4)',
                    fontWeight: 400,
                    fontSize: '1.5rem',
                    lineHeight: '40px',
                  }}
                >
                  –
                </Typography>
              )}

              {/* Home team */}
              {renderTeamInfo(game.homeTeam, true)}
            </Box>

            {/* Footer: duration (when known) on the left, action on the right */}
            {(end || isClickable) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pt: 1.5,
                  borderTop: '1px solid rgba(255,255,255,0.12)',
                }}
              >
                {end ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      color: 'rgba(255,255,255,0.6)',
                    }}
                  >
                    <VideocamOutlinedIcon sx={{ fontSize: 15 }} />
                    <Typography sx={{ fontSize: 11, letterSpacing: '0.15px', lineHeight: 1 }}>
                      {getDuration(start, end)}
                    </Typography>
                  </Box>
                ) : (
                  <Box />
                )}

                {isClickable && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      height: 26,
                      px: 1.5,
                      borderRadius: '100px',
                      backgroundColor: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      color: '#FFFFFF',
                      transition: 'background-color 0.2s ease, border-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: cx.blue.main,
                        borderColor: cx.blue.main,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        letterSpacing: '0.15px',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {gameStatus === 'live' ? 'Join Game' : 'Watch Game'}
                    </Typography>
                    <ChevronRightIcon sx={{ fontSize: 14 }} />
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Link>
    </Box>
  );
};

export default GameCard;
